import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, Check, Upload, Ruler, Square, Layers, Palette, ChevronRight, 
  Shield, Truck, HelpCircle, Image, X, Info, ThermometerSun,
  CircleDot, Maximize2, SlidersHorizontal, FileImage
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import SEO from '../components/SEO';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Color options
const COLORS = [
  { value: 'gris-anthracite', label: 'Gris anthracite', hex: '#374151' },
  { value: 'gris-clair', label: 'Gris clair', hex: '#9CA3AF' },
  { value: 'noir', label: 'Noir', hex: '#1F2937' },
  { value: 'marron', label: 'Marron', hex: '#78350F' },
  { value: 'beige', label: 'Beige', hex: '#D4C4A8' },
  { value: 'bleu-marine', label: 'Bleu marine', hex: '#1E3A5F' },
];

// Thickness options with descriptions and CM values
const THICKNESSES = [
  { value: 'standard', label: 'Standard – 10 cm', cm: '10', description: 'Isolation correcte pour un usage classique' },
  { value: 'renforcee', label: 'Renforcée – 12 cm', cm: '12', description: 'Meilleure conservation de la chaleur' },
  { value: 'haute-isolation', label: 'Haute isolation – 15 cm', cm: '15', description: 'Performance maximale, idéale en extérieur' },
];

// Skirt (bavette) heights
const SKIRT_HEIGHTS = [
  { value: '5', label: '5 cm' },
  { value: '10', label: '10 cm' },
  { value: '15', label: '15 cm' },
  { value: 'autre', label: 'Autre (préciser)' },
];

const CouvertureSurMesurePage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Contact info
    name: '',
    email: '',
    phone: '',
    // Dimensions
    longueur: '',
    largeur: '',
    angleType: 'droit',
    rayonAngle: '',
    // Options
    epaisseur: 'standard',
    couleur: 'gris-anthracite',
    bavette: 'non',
    bavetteHauteur: '',
    bavetteHauteurAutre: '',
    longueurSangles: '',
    attacheSecurite: 'non',
    // Additional
    message: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (uploadedImages.length + files.length > 5) {
      toast.error('Maximum 5 photos autorisées');
      return;
    }
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 10 Mo)`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImages(prev => [...prev, {
          name: file.name,
          data: event.target.result,
          file: file
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Get bavette height
    const bavetteHeight = formData.bavetteHauteur === 'autre' 
      ? formData.bavetteHauteurAutre 
      : formData.bavetteHauteur;

    // Build detailed message
    const detailsMessage = `
DEMANDE COUVERTURE SPA SUR MESURE
================================

DIMENSIONS DU SPA:
- Longueur: ${formData.longueur} cm
- Largeur: ${formData.largeur} cm
- Type d'angles: ${formData.angleType === 'droit' ? 'Angles droits' : 'Angles arrondis (radius)'}
${formData.angleType === 'radius' ? `- Rayon des angles: ${formData.rayonAngle || 'Non précisé'} cm` : ''}

OPTIONS CHOISIES:
- Épaisseur: ${THICKNESSES.find(t => t.value === formData.epaisseur)?.label || formData.epaisseur}
- Couleur: ${COLORS.find(c => c.value === formData.couleur)?.label || formData.couleur}
- Bavette: ${formData.bavette === 'oui' ? `Oui - Hauteur: ${bavetteHeight} cm` : 'Non'}
- Longueur sangles: ${formData.longueurSangles || 'Standard'}
- Attaches de sécurité: ${formData.attacheSecurite === 'oui' ? 'Oui' : 'Non'}

PHOTOS JOINTES: ${uploadedImages.length > 0 ? uploadedImages.map(img => img.name).join(', ') : 'Aucune'}

COMMENTAIRE DU CLIENT:
${formData.message || 'Aucun commentaire'}
    `.trim();

    try {
      // Upload images first if any
      let imageUrls = [];
      if (uploadedImages.length > 0) {
        // For now, we'll mention the images in the message
        // In production, you'd upload to a file storage service
      }

      await axios.post(`${API}/api/leads`, {
        type: 'devis-couverture-sur-mesure',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        product_name: 'Couverture spa sur mesure',
        message: detailsMessage,
      });

      setSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Couverture spa sur mesure – Fabrication personnalisée | Atlantique Spa Boutique"
        description="Couverture de spa sur mesure : fabrication adaptée aux dimensions de votre spa. Isolation thermique optimale, bavette, radius personnalisé. Devis gratuit."
        canonical="/couverture-spa-sur-mesure"
      />

      {/* Hero Section - Indigo/Purple accent for differentiation */}
      <div className="bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild><Link to="/" className="text-indigo-200 hover:text-white">Accueil</Link></BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-indigo-400" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild><Link to="/accessoires-spa" className="text-indigo-200 hover:text-white">Accessoires</Link></BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-indigo-400" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Couverture sur mesure</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="max-w-7xl mx-auto px-4 pb-16 pt-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Couverture de spa sur mesure
                <span className="block text-indigo-300 text-xl sm:text-2xl font-normal mt-3">Fabrication personnalisée</span>
              </h1>
              <p className="text-lg sm:text-xl text-indigo-100 mb-8 leading-relaxed">
                Une couverture adaptée à votre spa pour une meilleure isolation, 
                plus de confort et une durabilité renforcée.
              </p>
              <a href="#formulaire">
                <Button size="lg" className="bg-white text-indigo-900 hover:bg-indigo-50 font-semibold text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 gap-2">
                  Demander mon devis pour couverture sur mesure
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </a>
            </div>
            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/de76542d-5dc3-4caa-a205-525127775ff6/images/c959ee2edf90e3f6fe9f8e8b20f058db551d3c582753d2099cf8f89ca8b231ef.png"
                alt="Couverture de spa sur mesure"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="bg-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            Une <strong>couverture de spa sur mesure</strong> permet d'assurer une isolation optimale, 
            même lorsque les dimensions de votre spa ne sont pas standards. Elle contribue à 
            <strong> réduire les pertes de chaleur</strong>, protéger l'eau et améliorer le confort d'utilisation au quotidien.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="bg-white py-12 sm:py-16" id="formulaire">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Demande de devis</h2>
            <p className="text-base sm:text-lg text-slate-600">Remplissez ce formulaire pour recevoir votre devis personnalisé</p>
          </div>

          {submitted ? (
            <Card className="border-indigo-300 bg-indigo-50">
              <CardContent className="p-8 sm:p-10 text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Demande envoyée !</h3>
                <p className="text-slate-600 mb-6">
                  Nous avons bien reçu votre demande de devis pour une couverture sur mesure. 
                  Notre équipe vous recontactera sous 48h pour finaliser votre projet.
                </p>
                <Link to="/accessoires-spa">
                  <Button variant="outline">Retour aux accessoires</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <form onSubmit={handleSubmit}>
              <Card className="border-slate-200 shadow-lg">
                <CardContent className="p-5 sm:p-8">
                  {/* Contact Info */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                      <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xs font-bold">1</span>
                      Vos coordonnées
                    </h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="name">Nom complet *</Label>
                        <Input 
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                          placeholder="Jean Dupont"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input 
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                          placeholder="jean@exemple.fr"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Téléphone *</Label>
                        <Input 
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          required
                          placeholder="06 12 34 56 78"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dimensions */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                      <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xs font-bold">2</span>
                      Dimensions du spa
                    </h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="longueur">Longueur (cm) *</Label>
                        <Input 
                          id="longueur"
                          type="number"
                          value={formData.longueur}
                          onChange={(e) => handleChange('longueur', e.target.value)}
                          required
                          placeholder="200"
                          data-testid="input-longueur"
                        />
                      </div>
                      <div>
                        <Label htmlFor="largeur">Largeur (cm) *</Label>
                        <Input 
                          id="largeur"
                          type="number"
                          value={formData.largeur}
                          onChange={(e) => handleChange('largeur', e.target.value)}
                          required
                          placeholder="180"
                          data-testid="input-largeur"
                        />
                      </div>
                      <div>
                        <Label>Type d'angles *</Label>
                        <Select value={formData.angleType} onValueChange={(v) => handleChange('angleType', v)}>
                          <SelectTrigger data-testid="select-angle-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="droit">Angles droits</SelectItem>
                            <SelectItem value="radius">Radius (arrondis)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="rayonAngle" className="flex items-center gap-1">
                          Rayon d'angle (cm)
                          {formData.angleType === 'radius' && <span className="text-red-500"> *</span>}
                          <Dialog>
                            <DialogTrigger asChild>
                              <button 
                                type="button" 
                                className="ml-1 text-slate-400 hover:text-indigo-600 transition-colors"
                                aria-label="Aide sur le radius"
                              >
                                <HelpCircle className="w-4 h-4" />
                              </button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
                              <DialogHeader>
                                <DialogTitle className="text-xl font-bold text-indigo-700 pr-8">
                                  Comment mesurer l'angle ou le radius d'un spa ?
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 text-slate-700 text-sm sm:text-base">
                                <p>
                                  Le radius est un élément important dans la prise de mesure pour toutes les couvertures disposant d'angles arrondis.
                                </p>
                                
                                <div>
                                  <h4 className="font-semibold text-slate-900 mb-2">Qu'est-ce que le radius ?</h4>
                                  <p>
                                    La traduction littérale de radius est <strong>rayon</strong>. Cette mesure est nécessaire pour tous les spas disposant d'angles arrondis.
                                  </p>
                                </div>
                                
                                <p>
                                  La mesure du radius correspond à la longueur du rayon du cercle formé par la courbure de l'angle de votre spa.
                                </p>
                                
                                {/* Image explicative */}
                                <div className="mt-4 rounded-lg overflow-hidden border border-slate-200 bg-white">
                                  <img 
                                    src="https://customer-assets.emergentagent.com/job_43126d56-453b-434e-bb21-ea7c1a65734c/artifacts/hjmebcof_radius-spa.jpg"
                                    alt="Schéma explicatif : comment mesurer le radius d'un angle de spa"
                                    className="w-full h-auto object-contain"
                                  />
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </Label>
                        <Input 
                          id="rayonAngle"
                          type="number"
                          value={formData.rayonAngle}
                          onChange={(e) => handleChange('rayonAngle', e.target.value)}
                          placeholder="Ex: 10"
                          disabled={formData.angleType === 'droit'}
                          required={formData.angleType === 'radius'}
                          data-testid="input-rayon"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                      <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xs font-bold">3</span>
                      Options
                    </h3>
                    
                    <div className="grid sm:grid-cols-2 gap-6 mb-6">
                      {/* Épaisseur */}
                      <div>
                        <Label className="mb-3 block">Épaisseur souhaitée</Label>
                        <RadioGroup value={formData.epaisseur} onValueChange={(v) => handleChange('epaisseur', v)} className="space-y-2">
                          {THICKNESSES.map(t => (
                            <div key={t.value} className="flex items-center space-x-2">
                              <RadioGroupItem value={t.value} id={`epaisseur-${t.value}`} />
                              <Label htmlFor={`epaisseur-${t.value}`} className="font-normal cursor-pointer flex-1">
                                <span className="font-medium">{t.label}</span>
                                <span className="text-xs text-slate-500 block">{t.description}</span>
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>

                      {/* Couleur */}
                      <div>
                        <Label htmlFor="couleur" className="mb-3 block">Couleur souhaitée</Label>
                        <Select value={formData.couleur} onValueChange={(v) => handleChange('couleur', v)}>
                          <SelectTrigger data-testid="select-couleur">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COLORS.map(c => (
                              <SelectItem key={c.value} value={c.value}>
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: c.hex }}></div>
                                  {c.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Bavette */}
                    <div className="bg-slate-50 rounded-lg p-4 mb-6">
                      <Label className="mb-3 block font-semibold">Bavette (rebord latéral)</Label>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm text-slate-600">Souhaitez-vous une bavette ?</Label>
                          <Select value={formData.bavette} onValueChange={(v) => handleChange('bavette', v)}>
                            <SelectTrigger data-testid="select-bavette">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="non">Non</SelectItem>
                              <SelectItem value="oui">Oui</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {formData.bavette === 'oui' && (
                          <>
                            <div>
                              <Label className="text-sm text-slate-600">Hauteur de bavette</Label>
                              <Select value={formData.bavetteHauteur} onValueChange={(v) => handleChange('bavetteHauteur', v)}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                  {SKIRT_HEIGHTS.map(h => (
                                    <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {formData.bavetteHauteur === 'autre' && (
                              <div>
                                <Label className="text-sm text-slate-600">Précisez (cm)</Label>
                                <Input 
                                  type="number"
                                  value={formData.bavetteHauteurAutre}
                                  onChange={(e) => handleChange('bavetteHauteurAutre', e.target.value)}
                                  placeholder="Ex: 12"
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Other options */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="longueurSangles">Longueur des sangles (cm)</Label>
                        <Input 
                          id="longueurSangles"
                          type="number"
                          value={formData.longueurSangles}
                          onChange={(e) => handleChange('longueurSangles', e.target.value)}
                          placeholder="Standard si non précisé"
                          data-testid="input-sangles"
                        />
                      </div>
                      <div>
                        <Label className="mb-3 block">Attaches de sécurité</Label>
                        <Select value={formData.attacheSecurite} onValueChange={(v) => handleChange('attacheSecurite', v)}>
                          <SelectTrigger data-testid="select-securite">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="non">Non</SelectItem>
                            <SelectItem value="oui">Oui</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Photo Upload */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                      <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xs font-bold">4</span>
                      Photos de votre spa
                      <span className="text-sm font-normal text-slate-500">(fortement recommandé)</span>
                    </h3>
                    
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <FileImage className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-indigo-700">
                          Une photo nous aide à <strong>vérifier les dimensions</strong> et la forme des angles. 
                          Cela évite les erreurs et accélère la fabrication.
                        </p>
                      </div>
                    </div>

                    <div 
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="input-photos"
                      />
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-sm text-slate-600 mb-1">
                        <span className="text-indigo-600 font-medium">Cliquez pour ajouter</span> ou glissez vos photos
                      </p>
                      <p className="text-xs text-slate-500">PNG, JPG jusqu'à 10 Mo (max 5 photos)</p>
                    </div>

                    {/* Uploaded images preview */}
                    {uploadedImages.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        {uploadedImages.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={img.data} 
                              alt={img.name}
                              className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <span className="text-xs text-slate-500 block truncate w-20 mt-1">{img.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div className="mb-8">
                    <Label htmlFor="message">Commentaire ou informations complémentaires</Label>
                    <Textarea 
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Précisez ici toute information utile : modèle de spa, contraintes particulières..."
                      rows={4}
                      data-testid="input-message"
                    />
                  </div>

                  {/* Submit */}
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-base sm:text-lg py-6"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? 'Envoi en cours...' : 'Envoyer ma demande de devis'}
                  </Button>
                </CardContent>
              </Card>
            </form>
          )}
        </div>
      </div>

      {/* Help Block */}
      <div className="bg-indigo-50 py-10 sm:py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="border-indigo-200 bg-white">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-8 h-8 text-indigo-600" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Besoin d'aide pour les mesures ou les options ?</h3>
                <p className="text-slate-600">
                  Nous vous accompagnons pour <strong>vérifier les informations transmises avant fabrication</strong> afin d'éviter toute erreur.
                </p>
              </div>
              <div className="flex-shrink-0">
                <a href="mailto:contact@atlantiquespaboutique.com">
                  <Button variant="outline" className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    <Mail className="w-4 h-4" />
                    Nous contacter
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reassurance Block */}
      <div className="bg-white py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 text-center mb-8 sm:mb-10">
            Pourquoi choisir une couverture sur mesure ?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <Ruler className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-1 text-sm">Fabrication adaptée</h4>
              <p className="text-xs text-slate-600">Aux dimensions exactes de votre spa</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <ThermometerSun className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-1 text-sm">Isolation optimisée</h4>
              <p className="text-xs text-slate-600">Conservation maximale de la chaleur</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <HelpCircle className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-1 text-sm">Accompagnement</h4>
              <p className="text-xs text-slate-600">Vérification avant fabrication</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <Truck className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-1 text-sm">Livraison France</h4>
              <p className="text-xs text-slate-600">Expédition partout en France</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-1 text-sm">Service pro</h4>
              <p className="text-xs text-slate-600">Transparent et réactif</p>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Block */}
      <div className="bg-slate-50 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-slate max-w-none">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">
              Couverture de spa sur mesure : une isolation thermique adaptée à vos besoins
            </h2>
            <div className="text-slate-600 leading-relaxed space-y-4 text-sm sm:text-base">
              <p>
                La <strong>couverture spa sur mesure</strong> est la solution idéale lorsque votre spa présente 
                des dimensions non standards ou des angles particuliers. Contrairement aux couvertures prêtes à l'emploi, 
                une <strong>couverture thermique spa</strong> fabriquée sur mesure garantit un ajustement parfait, 
                sans espace ni pli susceptible de laisser s'échapper la chaleur.
              </p>
              <p>
                L'ajout d'une <strong>bavette couverture spa</strong> permet d'améliorer l'étanchéité en créant 
                un rebord qui descend sur les côtés de la cuve. Cette option est particulièrement recommandée 
                pour les spas installés en extérieur, où les courants d'air peuvent réduire l'efficacité de 
                l'<strong>isolation spa</strong>. Le choix du <strong>radius couverture spa</strong> dépend 
                de la forme des angles de votre équipement : angles droits ou arrondis.
              </p>
              <p>
                Le <strong>remplacement couverture spa</strong> devient nécessaire lorsque celle-ci montre 
                des signes d'usure : absorption d'eau, déformation, perte d'isolation. Une couverture 
                saturée d'eau perd son pouvoir isolant et alourdit considérablement la manipulation. 
                Opter pour une fabrication sur mesure permet de retrouver les performances d'origine 
                tout en adaptant les options à votre usage actuel.
              </p>
              <p>
                Atlantique Spa Boutique vous accompagne dans la configuration de votre nouvelle couverture : 
                prise de mesures, choix de l'épaisseur, coloris et finitions. N'hésitez pas à nous envoyer 
                une photo de votre spa pour faciliter l'étude de votre projet.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Final */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 py-10 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">Une question sur votre projet ?</h2>
          <p className="text-indigo-100 mb-6">Notre équipe est disponible pour vous accompagner</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button size="lg" variant="secondary" className="gap-2 w-full sm:w-auto">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <a href="#formulaire">
              <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 gap-2 w-full sm:w-auto">
                Demander un devis
                <ChevronRight className="w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default CouvertureSurMesurePage;
